import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Mic, Square, Volume2, VolumeX, Loader2, Search, AlertTriangle,
         TriangleAlert, CheckCircle2, Gauge, ClipboardList } from "lucide-react";

const MIME_CANDIDATES = ["audio/webm", "audio/mp4", "audio/ogg"];
const pickMime = () => MIME_CANDIDATES.find(
  (m) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) || "";
const extFor = (mime) => (mime.includes("mp4") ? "mp4" : mime.includes("ogg") ? "ogg" : "webm");

const EXAMPLES = [
  "Show me all colon cases from last month with positive margins",
  "Which STAT cases are still open",
  "Anything taking more than 3 days",
  "Breast cases signed out this week",
];

const SEVERITY = {
  critical: { chip: "bg-red-600 text-white", icon: AlertTriangle },
  warning: { chip: "bg-amber-500 text-slate-950", icon: TriangleAlert },
  ok: { chip: "bg-emerald-600 text-white", icon: CheckCircle2 },
};

const GROUP_ICON = { tat: Gauge, incomplete: ClipboardList, bottleneck: TriangleAlert };

export default function Assistant() {
  const [question, setQuestion] = useState("");
  const [result, setResult] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [busy, setBusy] = useState("");
  const [recording, setRecording] = useState(false);
  const [speakOn, setSpeakOn] = useState(true);
  const recorder = useRef(null);
  const chunks = useRef([]);
  const stream = useRef(null);
  const audio = useRef(null);

  const loadAnomalies = useCallback(async () => {
    try {
      const { data } = await api.get("/ai/anomalies");
      setAnomalies(data);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { loadAnomalies(); }, [loadAnomalies]);
  useEffect(() => () => {
    stream.current?.getTracks().forEach((t) => t.stop());
    audio.current?.pause();
  }, []);

  const say = async (text) => {
    if (!text) return;
    setBusy("speaking");
    try {
      const res = await api.post("/ai/speak", { text }, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(res.data);
      audio.current?.pause();
      audio.current = new Audio(url);
      audio.current.onended = () => URL.revokeObjectURL(url);
      await audio.current.play();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const ask = async (text, spoken = false) => {
    const q = (text ?? question).trim();
    if (!q) return;
    setBusy("search");
    setResult(null);
    try {
      const { data } = await api.post("/ai/search", { question: q }, { timeout: 120000 });
      setResult(data);
      if (spoken && speakOn) say(data.spoken);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const startRecording = async () => {
    try {
      const mime = pickMime();
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunks.current = [];
      const rec = new MediaRecorder(stream.current, mime ? { mimeType: mime } : undefined);
      rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      rec.onstop = async () => {
        stream.current?.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: mime || "audio/webm" });
        setBusy("listening");
        try {
          const form = new FormData();
          form.append("file", blob, `question.${extFor(mime || "audio/webm")}`);
          const { data } = await api.post("/ai/listen", form, {
            headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
          });
          if (!data.text) { toast.error("Nothing was picked up — try again closer to the microphone"); return; }
          setQuestion(data.text);
          await ask(data.text, true);
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
      };
      recorder.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone permission is needed to ask out loud");
    }
  };

  const stopRecording = () => {
    recorder.current?.stop();
    recorder.current = null;
    setRecording(false);
  };

  const working = busy === "search" || busy === "listening";

  return (
    <div className="space-y-6" data-testid="assistant-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          <Sparkles size={22} className="mr-2 inline text-[#134e4a]" />AI Assistant
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Ask about your cases in plain English, by typing or out loud. Answers are pulled straight from
          the case database — the assistant filters your data, it never writes diagnostic text.
        </p>
      </div>

      <section className="border border-slate-200 bg-white p-4 shadow-sm">
        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex flex-wrap gap-2">
          <Input value={question} onChange={(e) => setQuestion(e.target.value)}
                 data-testid="assistant-question-input" disabled={working}
                 placeholder="e.g. show me all colon cases from last month with positive margins"
                 className="h-12 min-w-[240px] flex-1 border-slate-300 text-base" />
          <Button type="submit" disabled={working || !question.trim()} data-testid="assistant-ask-button"
                  className="h-12 bg-[#134e4a] px-5 hover:bg-[#0f3f3c]">
            {busy === "search" ? <Loader2 size={17} className="animate-spin" /> : <Search size={17} />}
            <span className="ml-2">Ask</span>
          </Button>
          <Button type="button" onClick={recording ? stopRecording : startRecording} disabled={busy === "listening"}
                  data-testid="assistant-mic-button"
                  className={`h-12 px-5 ${recording ? "bg-red-600 hover:bg-red-500" : "bg-slate-800 hover:bg-slate-700"}`}>
            {busy === "listening" ? <Loader2 size={17} className="animate-spin" />
              : recording ? <Square size={17} /> : <Mic size={17} />}
            <span className="ml-2">{recording ? "Stop" : busy === "listening" ? "Transcribing…" : "Ask out loud"}</span>
          </Button>
          <Button type="button" variant="outline" onClick={() => setSpeakOn((s) => !s)}
                  data-testid="assistant-speak-toggle" className="h-12 border-slate-300 px-4"
                  title={speakOn ? "Spoken answers on" : "Spoken answers off"}>
            {speakOn ? <Volume2 size={17} /> : <VolumeX size={17} />}
          </Button>
        </form>
        <div className="mt-3 flex flex-wrap gap-2">
          {EXAMPLES.map((ex) => (
            <button key={ex} type="button" onClick={() => { setQuestion(ex); ask(ex); }} disabled={working}
                    data-testid={`assistant-example-${ex.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}
                    className="border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600 hover:border-[#134e4a] hover:text-[#134e4a]">
              {ex}
            </button>
          ))}
        </div>
        {recording && (
          <p className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-red-700" data-testid="assistant-recording">
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-600" /> Listening — press Stop when done
          </p>
        )}
      </section>

      {result && (
        <section className="overflow-hidden border border-slate-200 bg-white shadow-sm" data-testid="assistant-result">
          <header className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-3">
            <h2 className="text-base font-semibold text-slate-900">
              {result.unsupported ? "Cannot answer that from case data" : `${result.count}${result.truncated ? "+" : ""} case(s) — ${result.intent}`}
            </h2>
            <Button size="sm" variant="outline" onClick={() => say(result.spoken)} disabled={busy === "speaking"}
                    data-testid="assistant-speak-answer" className="ml-auto h-8 border-slate-300">
              {busy === "speaking" ? <Loader2 size={13} className="animate-spin" /> : <Volume2 size={13} />}
              <span className="ml-1.5">Read aloud</span>
            </Button>
          </header>
          {result.unsupported ? (
            <p className="px-4 py-4 text-sm text-slate-600" data-testid="assistant-unsupported">{result.unsupported}</p>
          ) : result.count === 0 ? (
            <p className="px-4 py-6 text-sm text-slate-600" data-testid="assistant-no-results">
              No cases match that. Try different wording, or widen the date range.
            </p>
          ) : (
            <div className="w-full max-w-full overflow-x-auto">
              <table className="w-full min-w-[680px] text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Accession</th><th className="px-3 py-2">Patient</th>
                    <th className="px-3 py-2">Specimen</th><th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Pathologist</th><th className="px-3 py-2 text-right">Open (h)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {result.cases.map((c) => (
                    <tr key={c.case_id} className="hover:bg-slate-50" data-testid={`assistant-row-${c.accession_number}`}>
                      <td className="px-3 py-2 font-mono font-semibold">
                        <Link to={`/cases/${c.case_id}`} className="text-[#134e4a] hover:underline">{c.accession_number}</Link>
                        {c.priority === "STAT" && <span className="ml-2 bg-red-600 px-1.5 text-[10px] font-bold text-white">STAT</span>}
                      </td>
                      <td className="px-3 py-2">{c.patient_name}</td>
                      <td className="px-3 py-2 text-slate-600">
                        {c.specimen_type}{c.specimen_site ? ` — ${c.specimen_site}` : ""}
                      </td>
                      <td className="px-3 py-2">{c.status}</td>
                      <td className="px-3 py-2 text-slate-600">{c.pathologist_name || "—"}</td>
                      <td className="num px-3 py-2 text-right">{c.hours_open ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section data-testid="assistant-anomalies">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-display text-lg font-bold text-slate-900">Anomalies &amp; alerts</h2>
          {anomalies && (
            <>
              <span className={`px-2 py-0.5 text-xs font-bold uppercase ${
                anomalies.counts.total === 0 ? SEVERITY.ok.chip
                  : anomalies.counts.stat_tat ? SEVERITY.critical.chip : SEVERITY.warning.chip}`}
                    data-testid="anomaly-total-chip">
                {anomalies.counts.total} finding{anomalies.counts.total === 1 ? "" : "s"}
              </span>
              <Button size="sm" variant="outline" onClick={() => say(anomalies.spoken_summary)}
                      disabled={busy === "speaking"} data-testid="anomaly-speak-button"
                      className="h-8 border-slate-300">
                <Volume2 size={13} className="mr-1.5" /> Read alerts aloud
              </Button>
              <span className="text-xs text-slate-500">
                Targets: {anomalies.targets.routine_hours} h routine · {anomalies.targets.stat_hours} h STAT
              </span>
            </>
          )}
        </div>

        <div className="mt-3 grid gap-4 lg:grid-cols-3">
          {(anomalies?.groups || []).map((g) => {
            const Icon = GROUP_ICON[g.kind] || TriangleAlert;
            const sev = SEVERITY[g.severity] || SEVERITY.ok;
            return (
              <div key={g.kind} className="border border-slate-200 bg-white shadow-sm" data-testid={`anomaly-group-${g.kind}`}>
                <header className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
                  <Icon size={15} className="text-slate-500" />
                  <h3 className="text-sm font-semibold text-slate-900">{g.title}</h3>
                  <span className={`ml-auto px-1.5 py-0.5 text-xs font-bold ${sev.chip}`}
                        data-testid={`anomaly-count-${g.kind}`}>{g.total}</span>
                </header>
                <ul className="max-h-72 divide-y divide-slate-100 overflow-y-auto">
                  {g.items.length === 0 && (
                    <li className="px-3 py-4 text-xs text-slate-500">Nothing flagged.</li>
                  )}
                  {g.items.map((it, i) => (
                    <li key={it.case_id || `${g.kind}-${i}`} className="px-3 py-2 text-xs">
                      {it.case_id ? (
                        <Link to={`/cases/${it.case_id}`} className="font-mono font-semibold text-[#134e4a] hover:underline">
                          {it.accession_number}
                        </Link>
                      ) : (
                        <span className="font-semibold text-slate-800">{it.stage}</span>
                      )}
                      {it.priority === "STAT" && <span className="ml-2 bg-red-600 px-1 text-[10px] font-bold text-white">STAT</span>}
                      {it.patient_name && <span className="ml-2 text-slate-700">{it.patient_name}</span>}
                      <p className="mt-0.5 text-slate-500">{it.detail}</p>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        {anomalies && (
          <p className="mt-2 text-xs text-slate-400">Checked {fmtDateTime(anomalies.generated_at)}</p>
        )}
      </section>
    </div>
  );
}
